import React from 'react';
import { Button, Icon, Row, Col } from 'antd';
import moment from 'moment';
import { withAppContext } from 'context/AppContext';
import { guard } from 'utils/general';

const TEAL = '#00b9c6';
const ACTIVE_SUBSCRIPTION_STATUSES = ['active', 'trialing', 'complete'];

const renewBoxActiveStyle = {
  marginTop: 16,
  marginBottom: 16,
  padding: '12px 14px',
  borderRadius: 8,
  background: 'rgba(0,185,198,0.08)',
  border: '1px solid rgba(0,185,198,0.35)'
};

const renewBoxCancelStyle = {
  ...renewBoxActiveStyle,
  background: 'rgba(250,173,20,0.12)',
  border: '1px solid rgba(250,173,20,0.55)'
};

const renewTitleStyle = {
  fontSize: 16,
  fontWeight: 700,
  marginBottom: 4
};

const renewSubStyle = {
  fontSize: 12,
  color: 'rgba(0,0,0,0.45)'
};

const formatRenewalMoment = endDate => moment.unix(endDate).utcOffset(8);

const renderRenewalBox = item => {
  const subscriptionStatus = guard(() => item.order.subscriptionStatus, '');
  const endDate = guard(() => item.order.endDate, '');
  const cancelAtEnd = Boolean(guard(() => item.order.cancelAtEndDate, false));
  const amount = guard(() => item.order.amount, null);
  const currency = guard(() => item.order.defaultCurrency, 'MYR');
  const isActivePeriod = ACTIVE_SUBSCRIPTION_STATUSES.includes(subscriptionStatus);
  const isCancelScheduled = isActivePeriod && cancelAtEnd;

  if (!endDate || !isActivePeriod) {
    return <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.45)', marginBottom: 16 }}>Subscription Expired</div>;
  }

  const renewalLabel = formatRenewalMoment(endDate).format('DD/MM/YYYY hh:mm A');

  if (isCancelScheduled) {
    return (
      <div style={renewBoxCancelStyle}>
        <div style={renewTitleStyle}>Ends on {renewalLabel}</div>
        <div style={renewSubStyle}>Access until period end · no further charges</div>
      </div>
    );
  }

  const end = formatRenewalMoment(endDate).startOf('minute');
  const now = moment()
    .utcOffset(8)
    .startOf('minute');
  const daysLeft = Math.max(0, Math.ceil(end.diff(now, 'days', true)));
  const code = currency === 'RM' ? 'MYR' : currency || 'MYR';
  const value = Number(amount);
  const chargeLabel = Number.isFinite(value) ? `${code} ${value.toFixed(2)}` : `${code} —`;

  return (
    <div style={renewBoxActiveStyle}>
      <div style={renewTitleStyle}>Renews on {renewalLabel}</div>
      <div style={renewSubStyle}>
        {daysLeft} days left · next charge {chargeLabel}
      </div>
    </div>
  );
};

const statusChipBaseStyle = {
  display: 'inline-block',
  marginLeft: 8,
  padding: '2px 10px',
  borderRadius: 4,
  fontSize: 14,
  lineHeight: '22px',
  fontWeight: 400
};

const STATUS_CHIP_STYLES = {
  active: {
    color: '#389e0d',
    background: '#f6ffed',
    border: '1px solid #b7eb8f'
  },
  expired: {
    color: '#cf1322',
    background: '#fff1f0',
    border: '1px solid #ffa39e'
  },
  cancelScheduled: {
    color: '#d48806',
    background: '#fffbe6',
    border: '1px solid #ffe58f'
  }
};

const StatusChip = ({ label, variant }) => (
  <span
    style={{
      ...statusChipBaseStyle,
      ...STATUS_CHIP_STYLES[variant]
    }}
  >
    {label}
  </span>
);

const renderStatusBadge = (subscriptionStatus, cancelAtEnd) => {
  const isActivePeriod = ACTIVE_SUBSCRIPTION_STATUSES.includes(subscriptionStatus);
  if (isActivePeriod && cancelAtEnd) {
    return <StatusChip label="Cancel scheduled" variant="cancelScheduled" />;
  }
  if (isActivePeriod) {
    return <StatusChip label="Active" variant="active" />;
  }
  return <StatusChip label="Expired" variant="expired" />;
};

const LinkProfileCard = props => (
  <div>
    <Row type="flex" gutter={[24, 24]} style={{ marginBottom: 24 }}>
      {props.data.map((item, index) => {
        const subscriptionStatus = guard(() => item.order.subscriptionStatus, '');
        const cancelAtEnd = Boolean(guard(() => item.order.cancelAtEndDate, false));

        return (
          <Col xs={24} md={12} xl={6} key={index} style={{ display: 'flex' }}>
            <div
              onClick={() => {
                props.setSelectCompanyProfile(item);
                const url = new URL(window.location.href);
                url.searchParams.set('_id', item._id);
                window.history.pushState({ path: url.toString() }, '', url.toString());
              }}
              style={{ padding: 24, border: `1px solid ${TEAL}`, borderRadius: 8, cursor: 'pointer', flex: 1 }}
            >
              <div style={{ textAlign: 'center' }}>
                <img src={guard(() => item.icon.url, '')} alt="logo" style={{ width: 160, height: 160, marginBottom: 16, objectFit: 'contain' }} />
              </div>

              <div style={{ fontSize: 18, fontWeight: 'bold' }}>{item.name}</div>
              <div style={{ fontSize: 14, color: '#666666' }}>Plan: {item.plan}</div>
              <div style={{ fontSize: 14, color: '#666666' }}>BRN: {item.brn}</div>
              <div style={{ fontSize: 14, color: '#666666', marginBottom: 12 }}>TIN: {item.tinNumber}</div>
              <div style={{ fontSize: 14, color: '#666666', marginBottom: 8 }}>Status: {renderStatusBadge(subscriptionStatus, cancelAtEnd)}</div>

              {renderRenewalBox(item)}

              <Button size="large" type="primary" block>
                View Company Profile
              </Button>
            </div>
          </Col>
        );
      })}

      {props.user.isAdmin || props.user.roles[0].permissions.includes(83) ? (
        <Col xs={24} md={12} xl={6} style={{ display: 'flex', height: '100%' }}>
          <div
            onClick={() => props.openLinkProfileModal()}
            style={{
              padding: 24,
              border: `1px solid ${TEAL}`,
              borderRadius: 8,
              textAlign: 'center',
              cursor: 'pointer',
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <Icon
              type="plus"
              style={{
                fontSize: '32px',
                color: TEAL,
                border: '1px dotted #00b9c6',
                borderRadius: 8,
                padding: 8,
                marginBottom: 16
              }}
            />

            <div style={{ fontSize: 24, fontWeight: 500 }}>Link Company Profile</div>
          </div>
        </Col>
      ) : null}
    </Row>
  </div>
);

export default withAppContext(LinkProfileCard);
